// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Canonical Gift-Card Brain (2026-08-15)
 * =============================================
 * ONE source of truth for "what is a gift card, is it taxed, and how do its dollars flow
 * through reporting". Pure logic — NO React, NO Firebase. Each app keeps its own
 * giftCardService (Firestore I/O) and imports THIS for categorization + money math + the
 * reporting readers, so POS, customer-portal and public-booking can never disagree.
 *
 * DO NOT EDIT the per-app copies in <app>/src/shared/ — edit THIS file at repo root and
 * run scripts/sync-shared.sh to propagate.
 *
 * Jose's accounting model (confirmed 2026-08-15) — the rules every surface must honor:
 *   1. A gift card is its OWN sale category. It is NOT a product and NOT a service.
 *   2. A gift card is NEVER taxed — not at sale, not ever. (Products are taxed; a PRODUCT
 *      bought later WITH a gift card still gets product tax — that tax is on the product
 *      line, independent of how it's paid. The gift-card sale itself is never taxed.)
 *   3. SOLD = revenue NOW. Selling a $100 card books $100 of revenue the day it's sold,
 *      on its own "Gift Cards Sold" line. "Once it's paid, we've earned it."
 *   4. REDEEMED ≠ new revenue. Using the card later was already counted at sale, so a
 *      redemption is a BALANCE DRAWDOWN — tracked and traceable, but NOT new revenue.
 *      To avoid double-counting, an appointment PAID BY gift-card redemption is excluded
 *      from shop net revenue — but the barber is still credited for the work (payroll is
 *      independent of when the cash was booked).
 *   5. A redemption may cover service + product + TIP (e.g. $60 card, $50 cut, $10 tip to
 *      use it up). The tip slice still goes to the barber.
 *   6. OUTSTANDING = the shop's liability = the sum of active card balances not yet redeemed.
 */

// ---------------------------------------------------------------------------
// Sale categories — gift card is first-class, alongside service and product.
// ---------------------------------------------------------------------------

export type SaleCategory = 'service' | 'product' | 'gift-card';

export const GIFT_CARD_CATEGORY: SaleCategory = 'gift-card';

/**
 * The ONE tax authority. Services and gift cards are never taxed; products are.
 * Centralized so no surface can accidentally tax a gift card (or forget to tax a product).
 */
export function isTaxable(category: SaleCategory): boolean {
  return category === 'product';
}

/** Tax for a single line, by category. Gift cards + services => always 0. */
export function lineTax(category: SaleCategory, price: number, taxRate: number): number {
  return isTaxable(category) ? round2(price * taxRate) : 0;
}

/**
 * Map a checkout line-item `type` ('service' | 'product' | 'giftcard') to a SaleCategory.
 * The checkout uses 'giftcard' (no hyphen) as its item type; normalize it here so there's
 * one spelling downstream.
 */
export function categoryOfItemType(itemType: string): SaleCategory {
  if (itemType === 'giftcard' || itemType === 'gift-card') return 'gift-card';
  if (itemType === 'product') return 'product';
  return 'service';
}

// ---------------------------------------------------------------------------
// Minimal shapes — each app feeds its own Firestore docs in (like AvailBarber does).
// ---------------------------------------------------------------------------

export type GiftCardStatus = 'pending' | 'active' | 'redeemed' | 'expired' | 'void';

/** Where a gift card was sold — so reporting can attribute sales per surface. */
export type SoldVia = 'pos' | 'portal' | 'public';

/** Just enough of a gift card to do sold/outstanding/redeemable math. */
export interface GCard {
  initialValue: number;
  currentBalance: number;
  status: GiftCardStatus;
  issuedDate?: DateLike;
  expirationDate?: DateLike;
}

/** Just enough of a gift-card transaction to do redeemed-in-range math. */
export interface GCTxn {
  type: 'purchase' | 'redemption' | 'refund' | 'adjustment';
  amount: number;        // redemption amounts are stored negative
  timestamp?: DateLike;
}

/** How a single redemption's dollars are split — for full traceability. */
export interface RedemptionBreakdown {
  service: number;
  product: number;
  tip: number;
}

// ---------------------------------------------------------------------------
// Redeemability — mirrors giftCardService.redeemGiftCard's own guards, so a card the
// picker shows as usable is actually redeemable (no drift between UI and write).
// ---------------------------------------------------------------------------

export function isRedeemable(card: GCard, now: Date = new Date()): boolean {
  if (card.status !== 'active') return false;
  if (card.currentBalance <= 0) return false;
  const exp = toDate(card.expirationDate);
  if (exp && now > exp) return false;
  return true;
}

/**
 * Split a bill against a card balance: the card covers up to its balance, the rest is owed
 * to another method. `fromCard` is what the card pays; `remainder` is what still needs
 * cash/card. Never returns more than the card holds or more than the bill.
 */
export function applyCardToTotal(
  billTotal: number,
  cardBalance: number,
): { fromCard: number; remainder: number } {
  const fromCard = round2(Math.max(0, Math.min(billTotal, cardBalance)));
  const remainder = round2(Math.max(0, billTotal - fromCard));
  return { fromCard, remainder };
}

/**
 * Given how much the card is paying and the bill's parts, allocate the card's dollars
 * across service -> product -> tip (in that order) so each redemption is fully traceable.
 * Sum of the returned parts always equals `fromCard`.
 */
export function splitRedemption(
  fromCard: number,
  parts: { service: number; product: number; tip: number },
): RedemptionBreakdown {
  let left = round2(Math.max(0, fromCard));
  const take = (want: number) => {
    const t = round2(Math.max(0, Math.min(left, want)));
    left = round2(left - t);
    return t;
  };
  const service = take(parts.service);
  const product = take(parts.product);
  const tip = take(parts.tip);
  return { service, product, tip };
}

// ---------------------------------------------------------------------------
// Reporting readers — the ONE place every report pulls gift-card numbers from.
// ---------------------------------------------------------------------------

/**
 * Gift Cards SOLD in a range = REVENUE (Jose's rule #3). Sum of initialValue for cards
 * issued in [from, to]. Sourced from the giftCards collection itself, so it counts sales
 * from every app (POS, portal, public) and never double-counts with appointment revenue.
 */
export function giftCardsSoldInRange(cards: GCard[], from: Date, to: Date): number {
  return round2(
    cards.reduce((sum, c) => {
      const d = toDate(c.issuedDate);
      return d && inRange(d, from, to) ? sum + (c.initialValue || 0) : sum;
    }, 0),
  );
}

/**
 * Gift Cards REDEEMED in a range = a DRAWDOWN, NOT new revenue (Jose's rule #4). Sum of
 * redemption amounts (stored negative -> abs) in [from, to]. Reported on its own line;
 * callers must NOT add this into revenue.
 */
export function giftCardsRedeemedInRange(txns: GCTxn[], from: Date, to: Date): number {
  return round2(
    txns.reduce((sum, t) => {
      if (t.type !== 'redemption') return sum;
      const d = toDate(t.timestamp);
      return d && inRange(d, from, to) ? sum + Math.abs(t.amount || 0) : sum;
    }, 0),
  );
}

/** Outstanding liability = money still owed as un-redeemed balance on active cards. */
export function outstandingLiability(cards: GCard[]): number {
  return round2(
    cards.reduce(
      (sum, c) => (c.status === 'active' ? sum + Math.max(0, c.currentBalance || 0) : sum),
      0,
    ),
  );
}

// ---------------------------------------------------------------------------
// Double-count guard (Jose's rule #4). Reporting uses this to EXCLUDE gift-card-redeemed
// sales from shop net revenue while STILL crediting the barber for the work.
// ---------------------------------------------------------------------------

/** True when an appointment/sale was paid by drawing down a gift card. */
export function isGiftCardRedemptionPayment(paymentMethod?: string | null): boolean {
  return paymentMethod === 'gift_card' || paymentMethod === 'giftcard';
}

// ---------------------------------------------------------------------------
// tiny pure date/number helpers (no external deps — keep this module self-contained)
// ---------------------------------------------------------------------------

/** Firestore dates arrive as Date | {toDate()} | ISO string, depending on the read path. */
export type DateLike = Date | string | number | { toDate: () => Date } | null | undefined;

export function toDate(v: DateLike): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && typeof (v as any).toDate === 'function') {
    const d = (v as any).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  const d = new Date(v as any);
  return isNaN(d.getTime()) ? null : d;
}

function inRange(d: Date, from: Date, to: Date): boolean {
  return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
